import { SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../Services/Context/AuthContext";
import { saveUserPreference } from "../Services/userPreferences";

function readStoredBoolean(storageKey: string, defaultValue: boolean): boolean {
  try {
    const storedValue = localStorage.getItem(storageKey);
    return storedValue === null ? defaultValue : storedValue === "true";
  } catch {
    return defaultValue;
  }
}

function writeStoredBoolean(storageKey: string, value: boolean): void {
  try {
    localStorage.setItem(storageKey, String(value));
  } catch { /* ignore */ }
}

export function usePersistentPreferences() {
  const { user } = useAuth();

  const [isReversedCardsEnabled, setIsReversedCardsEnabled] = useState<boolean>(
    () => readStoredBoolean("fatespin_reversed", true),
  );
  const [aiEnabled, setAiEnabled] = useState<boolean>(
    () => readStoredBoolean("fatespin_ai", true),
  );
  const syncedUserIdRef = useRef<string | undefined>(undefined);

  // Apply cloud preferences on login
  useEffect(() => {
    if (!user || user.id === syncedUserIdRef.current) return;
    syncedUserIdRef.current = user.id;
    const cloudReversed = user.user_metadata?.pref_reversed;
    const cloudAi = user.user_metadata?.pref_ai;
    if (typeof cloudReversed === "boolean") setIsReversedCardsEnabled(cloudReversed);
    if (typeof cloudAi === "boolean") setAiEnabled(cloudAi);
  }, [user]);

  useEffect(() => {
    writeStoredBoolean("fatespin_reversed", isReversedCardsEnabled);
  }, [isReversedCardsEnabled]);

  useEffect(() => {
    writeStoredBoolean("fatespin_ai", aiEnabled);
  }, [aiEnabled]);

  const setReversedWithSync = useCallback((value: SetStateAction<boolean>) => {
    const next = typeof value === "function" ? value(isReversedCardsEnabled) : value;
    setIsReversedCardsEnabled(next);
    if (user) saveUserPreference("pref_reversed", next);
  }, [user, isReversedCardsEnabled]);

  const setAiWithSync = useCallback((value: SetStateAction<boolean>) => {
    const next = typeof value === "function" ? value(aiEnabled) : value;
    setAiEnabled(next);
    if (user) saveUserPreference("pref_ai", next);
  }, [user, aiEnabled]);

  return {
    isReversedCardsEnabled,
    setIsReversedCardsEnabled: setReversedWithSync,
    aiEnabled,
    setAiEnabled: setAiWithSync,
  };
}
